import axios from 'axios';

/** CRA injects REACT_APP_API_URL. Must be absolute: https://host/api — if you omit https://,
 *  the browser treats the value as a path and you get .../dev-suite.../dev-suite.../api (405). */
function resolveApiBase(raw) {
  const fallback = 'http://localhost:8000';
  let u = (raw || fallback).trim().replace(/\/$/, '');
  if (!u) u = fallback;
  if (/^https?:\/\//i.test(u)) return u;
  // No scheme: axios resolves relative to page origin → duplicated hostname bug
  if (/^localhost\b|^127\.0\.0\.1(?::|$)/i.test(u)) return `http://${u}`;
  return `https://${u}`;
}

export const API_BASE = resolveApiBase(process.env.REACT_APP_API_URL);

/** Converts a stored upload path (e.g. "uploads/photos/abc.jpg") to a full URL served by the backend. */
export function getFileUrl(path) {
  if (!path) return null;
  if (path.startsWith('http')) return path;
  return `${API_BASE}/${path}`;
}
if (!process.env.REACT_APP_API_URL && process.env.NODE_ENV === 'production') {
  console.warn('[HRMS] REACT_APP_API_URL is not set. Falling back to http://localhost:8000 — this will fail in production.');
}

const api = axios.create({
  baseURL: API_BASE,
  headers: { 'Content-Type': 'application/json' },
  withCredentials: true, // send httpOnly cookie on every request (same-site); cross-origin often needs Bearer below
});

/** Same-host deployments use httpOnly cookies. Localhost → remote HTTPS API is cross-site: cookie may not attach to XHR; backend accepts Bearer too (utils/auth.py). */
const BEARER_STORAGE_KEY = 'hrms_bearer_token';

export function setBearerToken(token) {
  if (!token) return;
  api.defaults.headers.common.Authorization = `Bearer ${token}`;
  try {
    sessionStorage.setItem(BEARER_STORAGE_KEY, token);
  } catch (_) {}
}

export function clearBearerToken() {
  delete api.defaults.headers.common.Authorization;
  try {
    sessionStorage.removeItem(BEARER_STORAGE_KEY);
  } catch (_) {}
}

export function restoreBearerFromStorage() {
  try {
    const t = sessionStorage.getItem(BEARER_STORAGE_KEY);
    if (t) api.defaults.headers.common.Authorization = `Bearer ${t}`;
  } catch (_) {}
}

// Response interceptor — redirect to login on 401 (skip public auth pages)
const PUBLIC_AUTH_PATHS = ['/login', '/forgot-password', '/reset-password', '/change-password'];
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response && error.response.status === 401) {
      const onPublicPage = PUBLIC_AUTH_PATHS.some(p => window.location.pathname.startsWith(p));
      if (!onPublicPage) {
        window.location.href = '/login';
      }
    }
    return Promise.reject(error);
  }
);

// ─── Auth ──────────────────────────────────────────────────────────────────
export const auth = {
  login: (username, password) =>
    api.post('/auth/login', { username, password }),
  getMe: () => api.get('/auth/me'),
  logout: () => api.post('/auth/logout'),
  changePassword: (oldPassword, newPassword) =>
    api.post('/auth/change-password', { old_password: oldPassword, new_password: newPassword }),
  lookupResetUser: (identifier) =>
    api.post('/auth/lookup-reset-user', { identifier }),
  forgotPassword: (identifier) =>
    api.post('/auth/forgot-password', { identifier }),
  resetPassword: (token, newPassword) =>
    api.post('/auth/reset-password', { token, new_password: newPassword }),
};

// ─── Admin ─────────────────────────────────────────────────────────────────
export const admin = {
  getUsers: () => api.get('/admin/users'),
  createUser: (data) => api.post('/admin/users', data),
  updateUser: (id, data) => api.put(`/admin/users/${id}`, data),
  deleteUser: (id) => api.delete(`/admin/users/${id}`),
  getStats: () => api.get('/admin/stats'),
};

// ─── HRMS ──────────────────────────────────────────────────────────────────
export const hrms = {
  // Departments / Teams / Positions
  getDepartments: () => api.get('/hrms/departments'),
  createDepartment: (data) => api.post('/hrms/departments', data),
  updateDepartment: (id, data) => api.put(`/hrms/departments/${id}`, data),
  deleteDepartment: (id) => api.delete(`/hrms/departments/${id}`),
  getTeams: (deptId) => api.get('/hrms/teams', { params: deptId ? { department_id: deptId } : {} }),
  createTeam: (data) => api.post('/hrms/teams', data),
  updateTeam: (id, data) => api.put(`/hrms/teams/${id}`, data),
  deleteTeam: (id) => api.delete(`/hrms/teams/${id}`),
  getPositions: (deptId) => api.get('/hrms/positions', { params: deptId ? { department_id: deptId } : {} }),
  createPosition: (data) => api.post('/hrms/positions', data),
  updatePosition: (id, data) => api.put(`/hrms/positions/${id}`, data),
  deletePosition: (id) => api.delete(`/hrms/positions/${id}`),

  // Employees
  getEmployees: (params) => api.get('/hrms/employees', { params }),
  getNextEmpCode: (empType) => api.get('/hrms/employees/next-emp-code', { params: { emp_type: empType } }),
  createEmployee: (formData) =>
    api.post('/hrms/employees', formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  getEmployee: (id) => api.get(`/hrms/employees/${id}`),
  updateEmployee: (id, data) => api.put(`/hrms/employees/${id}`, data),
  getEmployeeHistory: (id) => api.get(`/hrms/employees/${id}/history`),

  // Bank Accounts
  getMyBankAccounts: ()         => api.get('/hrms/my-bank-accounts'),
  getBankAccounts:   (id)       => api.get(`/hrms/employees/${id}/bank-accounts`),
  addBankAccount:    (id, data) => api.post(`/hrms/employees/${id}/bank-accounts`, data),
  updateBankAccount: (id, bid, data) => api.put(`/hrms/employees/${id}/bank-accounts/${bid}`, data),
  deleteBankAccount: (id, bid)  => api.delete(`/hrms/employees/${id}/bank-accounts/${bid}`),

  // Employee Documents (PDF)
  getEmpDocuments:   (id)       => api.get(`/hrms/employees/${id}/documents`),
  uploadEmpDocument: (id, formData) => api.post(`/hrms/employees/${id}/documents`, formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  deleteEmpDocument: (id, did)  => api.delete(`/hrms/employees/${id}/documents/${did}`),

  // Nominees
  getNominees:   (id)           => api.get(`/hrms/employees/${id}/nominees`),
  addNominee:    (id, data)     => api.post(`/hrms/employees/${id}/nominees`, data),
  updateNominee: (id, nid, data)=> api.put(`/hrms/employees/${id}/nominees/${nid}`, data),
  deleteNominee: (id, nid)      => api.delete(`/hrms/employees/${id}/nominees/${nid}`),

  // Relationship options
  getRelationshipOptions: () => api.get('/hrms/relationship-options'),
  addEducation: (empId, data) => api.post(`/hrms/employees/${empId}/education`, data),
  updateEducation: (empId, eduId, data) => api.put(`/hrms/employees/${empId}/education/${eduId}`, data),
  deleteEducation: (empId, eduId) => api.delete(`/hrms/employees/${empId}/education/${eduId}`),
  addSkill: (empId, data) => api.post(`/hrms/employees/${empId}/skills`, data),
  deleteSkill: (empId, skillId) => api.delete(`/hrms/employees/${empId}/skills/${skillId}`),
  addCertification: (empId, formData) =>
    api.post(`/hrms/employees/${empId}/certifications`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    }),
  deleteCertification: (empId, certId) => api.delete(`/hrms/employees/${empId}/certifications/${certId}`),
  addExperience: (empId, data) => api.post(`/hrms/employees/${empId}/experience`, data),
  updateExperience: (empId, expId, data) => api.put(`/hrms/employees/${empId}/experience/${expId}`, data),
  deleteExperience: (empId, expId) => api.delete(`/hrms/employees/${empId}/experience/${expId}`),

  // Leave
  getLeaveTypes: () => api.get('/hrms/leave-types'),
  createLeaveType: (data) => api.post('/hrms/leave-types', data),
  updateLeaveType: (id, data) => api.put(`/hrms/leave-types/${id}`, data),
  deleteLeaveType: (id) => api.delete(`/hrms/leave-types/${id}`),
  getOrgChart: () => api.get('/hrms/org-chart'),
  getMyOrgPath: () => api.get('/hrms/my-org-path'),
  getLeaveBalances: (params) => api.get('/hrms/leave-balances', { params }),
  getMyLeaveBalances: () => api.get('/hrms/my-leave-balances'),
  initLeaveBalances: (data) => api.post('/hrms/leave-balances/initialize', data),
  getLeaveRequests: (params) => api.get('/hrms/leave-requests', { params }),
  createLeaveRequest: (data) => api.post('/hrms/leave-requests', data),
  getMyLeaveRequests: () => api.get('/hrms/leave-requests/my'),
  getTeamLeaveRequests: () => api.get('/hrms/leave-requests/team'),
  approveLeaveRequest: (id) => api.put(`/hrms/leave-requests/${id}/approve`),
  rejectLeaveRequest: (id, reason) => api.put(`/hrms/leave-requests/${id}/reject`, { rejection_reason: reason }),
  cancelLeaveRequest: (id) => api.put(`/hrms/leave-requests/${id}/cancel`),

  // WFH
  getWFHRequests: (params) => api.get('/hrms/wfh-requests', { params }),
  createWFHRequest: (data) => api.post('/hrms/wfh-requests', data),
  getMyWFHRequests: () => api.get('/hrms/wfh-requests/my'),
  getTeamWFHRequests: () => api.get('/hrms/wfh-requests/team'),
  approveWFHRequest: (id) => api.put(`/hrms/wfh-requests/${id}/approve`),
  rejectWFHRequest: (id, reason) => api.put(`/hrms/wfh-requests/${id}/reject`, { rejection_reason: reason }),
  cancelWFHRequest: (id) => api.put(`/hrms/wfh-requests/${id}/cancel`),

  // WFH Limits
  getWFHLimits: () => api.get('/hrms/wfh-limits'),
  getMyWFHLimit: () => api.get('/hrms/wfh-limits/my'),
  createWFHLimit: (data) => api.post('/hrms/wfh-limits', data),
  updateWFHLimit: (id, data) => api.put(`/hrms/wfh-limits/${id}`, data),
  deleteWFHLimit: (id) => api.delete(`/hrms/wfh-limits/${id}`),

  // Client Visit Requests
  getClientVisitRequests: (params) => api.get('/hrms/client-visit-requests', { params }),
  getTeamClientVisitRequests: (params) => api.get('/hrms/client-visit-requests/team', { params }),
  createClientVisitRequest: (data) => api.post('/hrms/client-visit-requests', data),
  approveClientVisitRequest: (id) => api.put(`/hrms/client-visit-requests/${id}/approve`),
  rejectClientVisitRequest: (id, reason) => api.put(`/hrms/client-visit-requests/${id}/reject`, { rejection_reason: reason }),
  cancelClientVisitRequest: (id) => api.put(`/hrms/client-visit-requests/${id}/cancel`),

  // Timesheet
  getTimesheetHistory:   (params) => api.get('/hrms/timesheet/history',            { params }),
  getPMTimesheet:        (params) => api.get('/hrms/timesheet/pm-view',            { params }),
  getAdminTimesheet:     (params) => api.get('/hrms/timesheet/admin',              { params }),
  submitTimesheet:       (week_start) => api.post('/hrms/timesheet/submit',        { week_start }),
  getWeekStatus:         (week_start) => api.get('/hrms/timesheet/week-status',    { params: { week_start } }),
  getPendingApprovals:          () => api.get('/hrms/timesheet/pending-approvals'),
  approveTimesheetWeek:         (id) => api.put(`/hrms/timesheet/weeks/${id}/approve`),
  approveTimesheetProject:      (id) => api.put(`/hrms/timesheet/project-approvals/${id}/approve`),
  rejectTimesheetProject:       (id, reason) => api.put(`/hrms/timesheet/project-approvals/${id}/reject`, { rejection_reason: reason }),
  rejectTimesheetWeek:   (id, reason) => api.put(`/hrms/timesheet/weeks/${id}/reject`, { rejection_reason: reason }),

  // Attendance
  getAttendance: (params) => api.get('/hrms/attendance', { params }),
  getMyAttendance: (params) => api.get('/hrms/attendance/my', { params }),
  markAttendance: (data) => api.post('/hrms/attendance', data),
  editAttendance: (id, data) => api.put(`/hrms/attendance/${id}`, data),
  deleteAttendance: (id) => api.delete(`/hrms/attendance/${id}`),
  getTodayAttendance: () => api.get('/hrms/attendance/today'),
  getAttendanceSummaryToday: () => api.get('/hrms/attendance/summary/today'),
  getAttendanceTodayEmployees: (status) => api.get('/hrms/attendance/summary/today/employees', { params: { status } }),
  checkIn: () => api.post('/hrms/attendance/checkin'),
  checkOut: () => api.post('/hrms/attendance/checkout'),
  getPendingManualAttendance: () => api.get('/hrms/attendance/pending-manual'),
  approveManualAttendance: (id) => api.post(`/hrms/attendance/${id}/approve`),
  rejectManualAttendance: (id, reason) => api.post(`/hrms/attendance/${id}/reject-manual`, { reason }),
  // Stage-1: manager endpoints
  getPendingManagerAttendance: () => api.get('/hrms/attendance/pending-manager'),
  managerApproveAttendance: (id) => api.post(`/hrms/attendance/${id}/manager-approve`),
  managerRejectAttendance: (id, reason) => api.post(`/hrms/attendance/${id}/manager-reject`, { reason }),

  // Grade Master
  getGrades: () => api.get('/hrms/grades'),
  createGrade: (data) => api.post('/hrms/grades', data),
  updateGrade: (id, data) => api.put(`/hrms/grades/${id}`, data),
  deleteGrade: (id) => api.delete(`/hrms/grades/${id}`),

  // Contractor Master
  getContractors:   ()         => api.get('/hrms/contractors'),
  createContractor: (data)     => api.post('/hrms/contractors', data),
  updateContractor: (id, data) => api.put(`/hrms/contractors/${id}`, data),
  deleteContractor: (id)       => api.delete(`/hrms/contractors/${id}`),

  // Timesheet not submitted
  getTimesheetNotSubmittedPrevWeek: () => api.get('/hrms/timesheet-admin/not-submitted-previous-week'),

  // Profile change requests
  submitProfileChangeRequest: (changes) => api.post('/hrms/profile/change-request', { changes }),
  getProfileChangeRequests: (status) => api.get('/hrms/profile/change-requests', { params: status ? { status } : {} }),
  approveProfileChangeRequest: (id) => api.post(`/hrms/profile/change-requests/${id}/approve`),
  rejectProfileChangeRequest: (id, reason) => api.post(`/hrms/profile/change-requests/${id}/reject`, { reason }),
  getMyProfileChangeRequests: () => api.get('/hrms/profile/my-change-requests'),

  // Projects
  getProjects: () => api.get('/hrms/projects'),
  createProject: (data) => api.post('/hrms/projects', data),
  updateProject: (id, data) => api.put(`/hrms/projects/${id}`, data),
  deleteProject: (id) => api.delete(`/hrms/projects/${id}`),
  assignEmployeeToProject: (projectId, data) => api.post(`/hrms/projects/${projectId}/assign`, data),
  getProjectEmployees: (projectId) => api.get(`/hrms/projects/${projectId}/assign`),
  removeEmployeeFromProject: (projectId, empProjectId) => api.delete(`/hrms/projects/${projectId}/assign/${empProjectId}`),
  getEmployeeProjects: (empId) => api.get(`/hrms/employees/${empId}/projects`),

  // Timesheet
  getTimesheetWeek: (weekStart) => api.get('/hrms/timesheet/week', { params: { week_start: weekStart } }),
  saveTimesheet: (data) => api.post('/hrms/timesheet/save', data),

  // Performance
  getPerformanceCycles: () => api.get('/hrms/performance-cycles'),
  createPerformanceCycle: (data) => api.post('/hrms/performance-cycles', data),
  getPerformanceReviews: (params) => api.get('/hrms/performance-reviews', { params }),
  createPerformanceReview: (data) => api.post('/hrms/performance-reviews', data),

  // Announcements
  getNotifications: () => api.get('/hrms/notifications'),
  getAnnouncements: () => api.get('/hrms/announcements'),
  createAnnouncement: (data) => api.post('/hrms/announcements', data),
  updateAnnouncement: (id, data) => api.put(`/hrms/announcements/${id}`, data),
  deleteAnnouncement: (id) => api.delete(`/hrms/announcements/${id}`),
  getTodayBirthdays: () => api.get('/hrms/announcements/today-birthdays'),
  sendBirthdayWish: (empId, message) => api.post(`/hrms/birthday-wish/${empId}`, { message }),
  getMyWishesSentToday: () => api.get('/hrms/birthday-wishes/my-wishes-sent'),

  // Hall of Fame
  getHallOfFame: () => api.get('/hrms/hall-of-fame'),
  createHallOfFame: (formData) => api.post('/hrms/hall-of-fame', formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  updateHallOfFame: (id, formData) => api.put(`/hrms/hall-of-fame/${id}`, formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  deleteHallOfFame: (id) => api.delete(`/hrms/hall-of-fame/${id}`),

  // Training
  getTrainingPrograms: (params) => api.get('/hrms/training', { params }),
  getTraining: (id) => api.get(`/hrms/training/${id}`),
  createTraining: (data) => api.post('/hrms/training', data),
  updateTraining: (id, data) => api.put(`/hrms/training/${id}`, data),
  deleteTraining: (id) => api.delete(`/hrms/training/${id}`),
  addTrainingParticipants: (id, data) => api.post(`/hrms/training/${id}/participants`, data),
  removeTrainingParticipant: (id, empId) => api.delete(`/hrms/training/${id}/participants/${empId}`),
  updateParticipantStatus: (id, empId, data) => api.put(`/hrms/training/${id}/participants/${empId}`, data),
  getMyTrainings: () => api.get('/hrms/training/my'),
  submitTrainingFeedback: (id, data) => api.post(`/hrms/training/${id}/feedback`, data),
  // Competency / Skill Matrix
  getCompetenciesList: (params) => api.get('/hrms/competencies-list', { params }),
  createCompetencyItem: (data) => api.post('/hrms/competencies-list', data),
  updateCompetencyItem: (id, data) => api.put(`/hrms/competencies-list/${id}`, data),
  deleteCompetencyItem: (id) => api.delete(`/hrms/competencies-list/${id}`),
  getSkillMatrix: (params) => api.get('/hrms/skill-matrix', { params }),
  rateCompetency: (data) => api.post('/hrms/skill-matrix/rate', data),

  // Payroll
  getMyPayslips: () => api.get('/hrms/my-payslips'),
  getPayrollPeriods: () => api.get('/hrms/payroll/periods'),
  getPayrollRecords: (params) => api.get('/hrms/payroll/records', { params }),
  runPayroll: (data) => api.post('/hrms/payroll/run', data),
  hrApprovePayroll: (periodId) => api.post(`/hrms/payroll/periods/${periodId}/hr-approve`),
  cfoApprovePayroll: (periodId) => api.post(`/hrms/payroll/periods/${periodId}/cfo-approve`),

  // Pay Fixation
  getPayFixations: (params) => api.get('/hrms/pay-fixations', { params }),
  createPayFixation: (data) => api.post('/hrms/pay-fixations', data),
  updatePayFixation: (id, data) => api.put(`/hrms/pay-fixations/${id}`, data),
  deletePayFixation: (id) => api.delete(`/hrms/pay-fixations/${id}`),

  // Promotions
  getPromotions: (params) => api.get('/hrms/promotions', { params }),
  createPromotion: (data) => api.post('/hrms/promotions', data),
  updatePromotion: (id, data) => api.put(`/hrms/promotions/${id}`, data),
  deletePromotion: (id) => api.delete(`/hrms/promotions/${id}`),

  // Transfers
  getTransfers: (params) => api.get('/hrms/transfers', { params }),
  createTransfer: (data) => api.post('/hrms/transfers', data),
  updateTransfer: (id, data) => api.put(`/hrms/transfers/${id}`, data),
  deleteTransfer: (id) => api.delete(`/hrms/transfers/${id}`),

  // Profile verification
  getProfileReviews: (params) => api.get('/hrms/profile-reviews', { params }),
  approveProfile: (empId) => api.put(`/hrms/employees/${empId}/profile-approve`),
  rejectProfile: (empId, data) => api.put(`/hrms/employees/${empId}/profile-reject`, data),

  // Employee self-service
  saveProfileDraft: (data) => {
    if (data instanceof FormData) return api.put('/hrms/profile/draft', data, { headers: { 'Content-Type': 'multipart/form-data' } });
    return api.put('/hrms/profile/draft', data);
  },
  completeProfile: (data) => api.put('/hrms/profile/complete', data),
  completeProfileWithPhoto: (formData) =>
    api.put('/hrms/profile/complete', formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  getMyEPFDeclaration: () => api.get('/hrms/epf-declaration/my'),
  saveEPFDeclaration: (data) => api.post('/hrms/epf-declaration', data),
  getMyESIDeclaration: () => api.get('/hrms/esi-declaration/my'),
  saveESIDeclaration: (data) => api.post('/hrms/esi-declaration', data),

  // Org Holidays
  getHolidays: (year) => api.get('/hrms/holidays', { params: { year } }),
  createHoliday: (data) => api.post('/hrms/holidays', data),
  updateHoliday: (id, data) => api.put(`/hrms/holidays/${id}`, data),
  deleteHoliday: (id) => api.delete(`/hrms/holidays/${id}`),

  // Interviews – candidates
  getCandidates: (params) => api.get('/hrms/candidates', { params }),
  getCandidate: (id) => api.get(`/hrms/candidates/${id}`),
  createCandidate: (data) => api.post('/hrms/candidates', data),
  updateCandidate: (id, data) => api.put(`/hrms/candidates/${id}`, data),
  deleteCandidate: (id) => api.delete(`/hrms/candidates/${id}`),
  onboardCandidate: (id) => api.post(`/hrms/candidates/${id}/onboard`),
  getCandidateDocs: (id) => api.get(`/hrms/candidates/${id}/documents`),
  uploadCandidateDoc: (id, formData) => api.post(`/hrms/candidates/${id}/documents`, formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  deleteCandidateDoc: (candId, docId) => api.delete(`/hrms/candidates/${candId}/documents/${docId}`),
  // Interviews – rounds
  getInterviewRounds: (candidateId) => api.get(`/hrms/candidates/${candidateId}/rounds`),
  addInterviewRound: (candidateId, data) => api.post(`/hrms/candidates/${candidateId}/rounds`, data),
  updateInterviewRound: (candidateId, roundId, data) => api.put(`/hrms/candidates/${candidateId}/rounds/${roundId}`, data),
  deleteInterviewRound: (candidateId, roundId) => api.delete(`/hrms/candidates/${candidateId}/rounds/${roundId}`),
  // Interviews – offers
  createOffer: (candidateId, data) => api.post(`/hrms/candidates/${candidateId}/offer`, data),
  updateOffer: (candidateId, data) => api.put(`/hrms/candidates/${candidateId}/offer`, data),
  deleteOffer: (candidateId) => api.delete(`/hrms/candidates/${candidateId}/offer`),
  // Requisitions
  getRequisitions: (params) => api.get('/hrms/requisitions', { params }),
  createRequisition: (data) => api.post('/hrms/requisitions', data),
  updateRequisition: (id, data) => api.put(`/hrms/requisitions/${id}`, data),
  deleteRequisition: (id) => api.delete(`/hrms/requisitions/${id}`),
  // Analytics
  getInterviewAnalytics: () => api.get('/hrms/interview-analytics'),
};

// ─── Interview Management (Extended) ──────────────────────────────────────
export const interviews = {
  // Dashboard
  getDashboardStats: () => api.get('/interviews/dashboard-stats'),
  getPipeline: () => api.get('/interviews/pipeline'),

  // Panels
  getPanels: (params) => api.get('/interviews/panels', { params }),
  getPanel: (id) => api.get(`/interviews/panels/${id}`),
  createPanel: (data) => api.post('/interviews/panels', data),
  updatePanel: (id, data) => api.put(`/interviews/panels/${id}`, data),
  deletePanel: (id) => api.delete(`/interviews/panels/${id}`),
  addPanelMember: (panelId, data) => api.post(`/interviews/panels/${panelId}/members`, data),
  removePanelMember: (panelId, userId) => api.delete(`/interviews/panels/${panelId}/members/${userId}`),
  clonePanel: (panelId) => api.post(`/interviews/panels/${panelId}/clone`),
  getMyPanels: () => api.get('/interviews/panels/my-panels'),

  // People for panel selection (accessible by hr_admin / reporting_manager)
  getEmployeesForPanel: () => api.get('/interviews/employees-for-panel'),

  // Scheduling
  getMySchedule: () => api.get('/interviews/my-schedule'),
  checkConflict: (params) => api.get('/interviews/conflict-check', { params }),

  // Candidate extended
  getCandidateEventLog: (id) => api.get(`/interviews/candidates/${id}/event-log`),
  assignPanel: (candidateId, panelId) =>
    api.post(`/interviews/candidates/${candidateId}/assign-panel`, null, { params: { panel_id: panelId } }),

  // Templates
  getTemplates: (params) => api.get('/interviews/templates', { params }),
  getTemplate: (id) => api.get(`/interviews/templates/${id}`),
  createTemplate: (data) => api.post('/interviews/templates', data),
  updateTemplate: (id, data) => api.put(`/interviews/templates/${id}`, data),
  deleteTemplate: (id) => api.delete(`/interviews/templates/${id}`),
  addCriterion: (templateId, data) => api.post(`/interviews/templates/${templateId}/criteria`, data),
  updateCriterion: (criterionId, data) => api.put(`/interviews/templates/criteria/${criterionId}`, data),
  deleteCriterion: (criterionId) => api.delete(`/interviews/templates/criteria/${criterionId}`),

  // Question Bank
  getQuestions: (params) => api.get('/interviews/questions', { params }),
  createQuestion: (data) => api.post('/interviews/questions', data),
  updateQuestion: (id, data) => api.put(`/interviews/questions/${id}`, data),
  deleteQuestion: (id) => api.delete(`/interviews/questions/${id}`),

  // Scorecards
  getRoundScorecards: (roundId) => api.get(`/interviews/scorecards/round/${roundId}`),
  getRoundAggregate: (roundId) => api.get(`/interviews/scorecards/round/${roundId}/aggregate`),
  getScorecard: (id) => api.get(`/interviews/scorecards/${id}`),
  createScorecard: (data) => api.post('/interviews/scorecards', data),
  updateScorecard: (id, data) => api.put(`/interviews/scorecards/${id}`, data),
  submitScorecard: (id) => api.post(`/interviews/scorecards/${id}/submit`),

  // Feedback/SLA
  getPendingFeedback: () => api.get('/interviews/feedback/pending'),
  getMyPendingFeedback: () => api.get('/interviews/feedback/my-pending'),
  getSLAConfig: () => api.get('/interviews/feedback/sla-config'),
  updateSLAConfig: (data) => api.put('/interviews/feedback/sla-config', data),

  // Hiring Decisions
  getDecisions: (params) => api.get('/interviews/decisions', { params }),
  getDecision: (id) => api.get(`/interviews/decisions/${id}`),
  createDecision: (data) => api.post('/interviews/decisions', data),
  getMyApprovals: () => api.get('/interviews/decisions/my-approvals'),
  approveDecision: (id, comments) =>
    api.post(`/interviews/decisions/${id}/approve`, null, { params: comments ? { comments } : {} }),
  rejectDecision: (id, comments) =>
    api.post(`/interviews/decisions/${id}/reject`, null, { params: { comments } }),

  // Salary Grades
  getSalaryGrades: () => api.get('/interviews/salary-grades'),
  createSalaryGrade: (data) => api.post('/interviews/salary-grades', data),
  updateSalaryGrade: (id, data) => api.put(`/interviews/salary-grades/${id}`, data),

  // Offers
  getOffers: (params) => api.get('/interviews/offers', { params }),
  getOffer: (id) => api.get(`/interviews/offers/${id}`),
  createOffer: (data) => api.post('/interviews/offers', data),
  updateOffer: (id, data) => api.put(`/interviews/offers/${id}`, data),
  submitOfferForApproval: (id) => api.post(`/interviews/offers/${id}/submit-for-approval`),
  approveOffer: (id) => api.post(`/interviews/offers/${id}/approve`),
  sendOffer: (id) => api.post(`/interviews/offers/${id}/send`),
  recordOfferResponse: (id, response) =>
    api.post(`/interviews/offers/${id}/record-response`, null, { params: { response } }),
  revokeOffer: (id, reason) =>
    api.post(`/interviews/offers/${id}/revoke`, null, { params: { reason } }),
};

// ─── IT ────────────────────────────────────────────────────────────────────
export const it = {
  getAssetCategories: () => api.get('/it/categories'),
  createAssetCategory: (data) => api.post('/it/categories', data),
  updateAssetCategory: (id, data) => api.put(`/it/categories/${id}`, data),
  deleteAssetCategory: (id) => api.delete(`/it/categories/${id}`),
  getAssets: (params) => api.get('/it/assets', { params }),
  getNextAssetCode: (prefix, category_id) => api.get('/it/assets/next-code', { params: { prefix, category_id } }),
  createAsset: (data) => api.post('/it/assets', data),
  getAsset: (id) => api.get(`/it/assets/${id}`),
  updateAsset: (id, data) => api.put(`/it/assets/${id}`, data),
  getMyAssets: () => api.get('/it/assets/my'),
  assignAsset: (id, data) => api.post(`/it/assets/${id}/assign`, data),
  returnAsset: (id, data) => api.post(`/it/assets/${id}/return`, data),
  transferAsset: (id, data) => api.post(`/it/assets/${id}/transfer`, data),
  getLicenses: (params) => api.get('/it/licenses', { params }),
  getLicense: (id) => api.get(`/it/licenses/${id}`),
  createLicense: (data) => api.post('/it/licenses', data),
  updateLicense: (id, data) => api.put(`/it/licenses/${id}`, data),
  deleteLicense: (id) => api.delete(`/it/licenses/${id}`),
  assignLicense: (id, data) => api.post(`/it/licenses/${id}/assign`, data),
  revokeLicense: (licId, assignmentId) => api.post(`/it/licenses/${licId}/revoke/${assignmentId}`),
  getTickets: (params) => api.get('/it/tickets', { params }),
  createTicket: (data) => api.post('/it/tickets', data),
  getMyTickets: () => api.get('/it/tickets/my'),
  getTicket: (id) => api.get(`/it/tickets/${id}`),
  updateTicket: (id, data) => api.put(`/it/tickets/${id}`, data),
  acknowledgeTicket: (id) => api.put(`/it/tickets/${id}/acknowledge`),
  reopenTicket: (id) => api.put(`/it/tickets/${id}/reopen`),
  addTicketComment: (id, comment) => api.post(`/it/tickets/${id}/comments`, { comment }),
  getTicketComments: (id) => api.get(`/it/tickets/${id}/comments`),
  getAssetQR: (id) => api.get(`/it/assets/${id}/qr`),
  bulkImportAssets: (formData) =>
    api.post('/it/assets/bulk-import', formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  getITDashboard: () => api.get('/it/dashboard'),
  getITStaff: () => api.get('/it/staff'),
  getMyTicketQueue: () => api.get('/it/tickets/my-queue'),
  exportAssetsCSV: () => api.get('/it/assets/export', { responseType: 'blob' }),
  getEmployeeAssets: (empId) => api.get(`/it/employee-assets/${empId}`),
};

// ─── Performance / Self-Assessment ─────────────────────────────────────────
export const performance = {
  // Assessment Forms (HR)
  getForms: () => api.get('/performance/forms'),
  createForm: (data) => api.post('/performance/forms', data),
  updateForm: (id, data) => api.put(`/performance/forms/${id}`, data),
  deleteForm: (id) => api.delete(`/performance/forms/${id}`),
  addField: (formId, data) => api.post(`/performance/forms/${formId}/fields`, data),
  updateField: (formId, fieldId, data) => api.put(`/performance/forms/${formId}/fields/${fieldId}`, data),
  deleteField: (formId, fieldId) => api.delete(`/performance/forms/${formId}/fields/${fieldId}`),

  // Cycles (HR)
  getCycles: () => api.get('/performance/cycles'),
  createCycle: (data) => api.post('/performance/cycles', data),
  launchCycle: (cycleId, data) => api.post(`/performance/cycles/${cycleId}/launch`, data),

  // Employee
  getMyAssessments: () => api.get('/performance/my-assessments'),
  getAssessment: (id) => api.get(`/performance/assessments/${id}`),
  saveAssessment: (id, data) => api.put(`/performance/assessments/${id}/save`, data),

  // Manager
  getTeamAssessments: () => api.get('/performance/team-assessments'),
  submitReview: (id, data) => api.put(`/performance/assessments/${id}/review`, data),

  // HR Monitor
  getAllAssessments: (params) => api.get('/performance/all-assessments', { params }),
  getStats: (params) => api.get('/performance/stats', { params }),

  // Goals V2
  getGoals: (params) => api.get('/performance/goals', { params }),
  getMyGoals: (params) => api.get('/performance/my-goals', { params }),
  getTeamGoals: (params) => api.get('/performance/team-goals', { params }),
  createGoal: (data) => api.post('/performance/goals', data),
  updateGoal: (id, data) => api.put(`/performance/goals/${id}`, data),
  deleteGoal: (id) => api.delete(`/performance/goals/${id}`),
  addCheckin: (goalId, data) => api.post(`/performance/goals/${goalId}/checkin`, data),
  getCheckins: (goalId) => api.get(`/performance/goals/${goalId}/checkins`),

  // Feedback & Journal
  giveFeedback: (data) => api.post('/performance/feedback', data),
  getReceivedFeedback: () => api.get('/performance/feedback/received'),
  getMyJournal: () => api.get('/performance/feedback/my-journal'),
  getTeamFeedback: () => api.get('/performance/team-feedback'),

  // Competencies
  getCompetencies: () => api.get('/performance/competencies'),
  createCompetency: (data) => api.post('/performance/competencies', data),
  updateCompetency: (id, data) => api.put(`/performance/competencies/${id}`, data),
  deleteCompetency: (id) => api.delete(`/performance/competencies/${id}`),
  selfRateCompetency: (data) => api.post('/performance/competencies/self-rate', data),
  rateCompetency: (data) => api.post('/performance/competencies/rate', data),
  getMyCompetencyRatings: (params) => api.get('/performance/competencies/my-ratings', { params }),

  // Analytics
  getAnalyticsOverview: (params) => api.get('/performance/analytics/overview', { params }),
  getTeamAnalytics: (params) => api.get('/performance/analytics/team', { params }),
};

// ─── Claims ────────────────────────────────────────────────────────────────
export const claims = {
  getClaims: (params) => api.get('/claims', { params }),
  submitClaim: (formData) =>
    api.post('/claims', formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  saveDraft: (formData) =>
    api.post('/claims/draft', formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  submitDraft: (id) => api.post(`/claims/${id}/submit`),
  getMyClaims: () => api.get('/claims/my'),
  getClaim: (id) => api.get(`/claims/${id}`),
  approveClaim: (id, data) => api.put(`/claims/${id}/approve`, data),
  rejectClaim: (id, reason) => api.put(`/claims/${id}/reject`, { reason }),
  cancelClaim: (id) => api.put(`/claims/${id}/cancel`),
  editClaim: (id, formData) =>
    api.put(`/claims/${id}/edit`, formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
};

// ─── Finance ───────────────────────────────────────────────────────────────
export const finance = {
  getFinanceClaims: (params) => api.get('/finance/claims', { params }),
  getClaimDetail: (id) => api.get(`/finance/claims/${id}`),
  approveClaim: (id) => api.put(`/finance/claims/${id}/approve`),
  rejectClaim: (id, reason) => api.put(`/finance/claims/${id}/reject`, { reason }),
  modifyClaim: (id, data) => api.put(`/finance/claims/${id}/modify`, data),
  verifyClaim: (id, data) => api.put(`/finance/claims/${id}/verify`, data),
  markClaimPaid: (id) => api.put(`/finance/claims/${id}/mark-paid`),
  mergeClaims: (data) => api.post('/finance/claims/merge', data),
  getMonthlyReport: (params) => api.get('/finance/claims/monthly-report', { params }),
  downloadMonthlyClaimsReport: (params) =>
    api.get('/finance/claims/monthly-report-download', { params, responseType: 'blob' }),
  getFinanceDashboard: () => api.get('/finance/dashboard'),
};

// ─── CEO ───────────────────────────────────────────────────────────────────
export const ceoApi = {
  getDashboard: () => api.get('/ceo/dashboard'),
};

// ─── Project Management (PM) ───────────────────────────────────────────────
export const pm = {
  // ── Projects ──────────────────────────────────────────────────────────────
  getProjects:        (params)         => api.get('/pm/projects', { params }),
  getProject:         (id)             => api.get(`/pm/projects/${id}`),
  createProject:      (data)           => api.post('/pm/projects', data),
  updateProject:      (id, data)       => api.put(`/pm/projects/${id}`, data),
  uploadProjectLogo:  (id, formData)   => api.post(`/pm/projects/${id}/upload-logo`, formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  deleteProject:      (id)             => api.delete(`/pm/projects/${id}`),
  getProjectDashboard:(id)             => api.get(`/pm/projects/${id}/dashboard`),

  // ── Members ───────────────────────────────────────────────────────────────
  getMembers:              (pid)           => api.get(`/pm/projects/${pid}/members`),
  getAssignableEmployees:  (pid)           => api.get(`/pm/projects/${pid}/assignable-employees`),
  addMember:               (pid, data)     => api.post(`/pm/projects/${pid}/members`, data),
  updateMember:            (pid, mid, data)=> api.put(`/pm/projects/${pid}/members/${mid}`, data),
  removeMember:            (pid, mid)      => api.delete(`/pm/projects/${pid}/members/${mid}`),

  // ── States ────────────────────────────────────────────────────────────────
  getStates:    (pid)        => api.get(`/pm/projects/${pid}/states`),
  createState:  (pid, data)  => api.post(`/pm/projects/${pid}/states`, data),
  updateState:  (sid, data)  => api.put(`/pm/states/${sid}`, data),
  deleteState:  (sid)        => api.delete(`/pm/states/${sid}`),

  // ── Labels ────────────────────────────────────────────────────────────────
  getLabels:    (pid)        => api.get(`/pm/projects/${pid}/labels`),
  createLabel:  (pid, data)  => api.post(`/pm/projects/${pid}/labels`, data),
  updateLabel:  (lid, data)  => api.put(`/pm/labels/${lid}`, data),
  deleteLabel:  (lid)        => api.delete(`/pm/labels/${lid}`),

  // ── Issues ────────────────────────────────────────────────────────────────
  getIssues:    (pid, params)  => api.get(`/pm/projects/${pid}/issues`, { params }),
  createIssue:  (pid, data)    => api.post(`/pm/projects/${pid}/issues`, data),
  getIssue:     (iid)          => api.get(`/pm/issues/${iid}`),
  updateIssue:  (iid, data)    => api.put(`/pm/issues/${iid}`, data),
  deleteIssue:  (iid)          => api.delete(`/pm/issues/${iid}`),

  // ── Comments ──────────────────────────────────────────────────────────────
  getComments:  (iid)        => api.get(`/pm/issues/${iid}/comments`),
  addComment:   (iid, data)  => api.post(`/pm/issues/${iid}/comments`, data),
  deleteComment:(cid)        => api.delete(`/pm/comments/${cid}`),

  // ── Sprints ───────────────────────────────────────────────────────────────
  getSprints:             (pid)          => api.get(`/pm/projects/${pid}/cycles`),
  createSprint:           (pid, data)    => api.post(`/pm/projects/${pid}/cycles`, data),
  getSprint:              (cid)          => api.get(`/pm/cycles/${cid}`),
  updateSprint:           (cid, data)    => api.put(`/pm/cycles/${cid}`, data),
  deleteSprint:           (cid)          => api.delete(`/pm/cycles/${cid}`),
  getSprintIssues:        (cid)          => api.get(`/pm/cycles/${cid}/issues`),
  addIssuesToSprint:      (cid, data)    => api.post(`/pm/cycles/${cid}/issues`, data),
  removeIssueFromSprint:  (cid, iid)     => api.delete(`/pm/cycles/${cid}/issues/${iid}`),

  // ── Modules ───────────────────────────────────────────────────────────────
  getModules:            (pid)          => api.get(`/pm/projects/${pid}/modules`),
  createModule:          (pid, data)    => api.post(`/pm/projects/${pid}/modules`, data),
  getModule:             (mid)          => api.get(`/pm/modules/${mid}`),
  updateModule:          (mid, data)    => api.put(`/pm/modules/${mid}`, data),
  deleteModule:          (mid)          => api.delete(`/pm/modules/${mid}`),
  getModuleIssues:       (mid)          => api.get(`/pm/modules/${mid}/issues`),
  addIssuesToModule:     (mid, data)    => api.post(`/pm/modules/${mid}/issues`, data),
  removeIssueFromModule: (mid, iid)     => api.delete(`/pm/modules/${mid}/issues/${iid}`),

  // ── Milestones ────────────────────────────────────────────────────────────
  getMilestones:            (pid)       => api.get(`/pm/projects/${pid}/milestones`),
  createMilestone:          (pid, data) => api.post(`/pm/projects/${pid}/milestones`, data),
  getMilestone:             (mid)       => api.get(`/pm/milestones/${mid}`),
  updateMilestone:          (mid, data) => api.put(`/pm/milestones/${mid}`, data),
  deleteMilestone:          (mid)       => api.delete(`/pm/milestones/${mid}`),
  getMilestoneIssues:       (mid)       => api.get(`/pm/milestones/${mid}/issues`),
  addIssuesToMilestone:     (mid, data) => api.post(`/pm/milestones/${mid}/issues`, data),
  removeIssueFromMilestone: (mid, iid)  => api.delete(`/pm/milestones/${mid}/issues/${iid}`),

  // ── Issue Activity ────────────────────────────────────────────────────────
  getIssueActivities: (iid) => api.get(`/pm/issues/${iid}/activities`),

  // ── Issue Relations (blocking / depends-on) ───────────────────────────────
  getProjectRelations: (pid)        => api.get(`/pm/projects/${pid}/issue-relations`),
  getIssueRelations:   (iid)        => api.get(`/pm/issues/${iid}/relations`),
  addIssueRelation:    (iid, data)  => api.post(`/pm/issues/${iid}/relations`, data),
  deleteIssueRelation: (rid)        => api.delete(`/pm/issue-relations/${rid}`),

  // ── My work ───────────────────────────────────────────────────────────────
  getMyIssues: () => api.get('/pm/my-issues'),

  // ── Bugs ──────────────────────────────────────────────────────────────────
  getBugs:          (pid, params)  => api.get(`/pm/projects/${pid}/bugs`, { params }),
  createBug:        (pid, data)    => api.post(`/pm/projects/${pid}/bugs`, data),
  getBugAnalytics:  (pid)          => api.get(`/pm/projects/${pid}/bugs/analytics`),
  getBug:           (id)           => api.get(`/pm/bugs/${id}`),
  updateBug:        (id, data)     => api.put(`/pm/bugs/${id}`, data),
  deleteBug:        (id)           => api.delete(`/pm/bugs/${id}`),
  reopenBug:        (id)           => api.post(`/pm/bugs/${id}/reopen`),
  getBugComments:   (id)           => api.get(`/pm/bugs/${id}/comments`),
  addBugComment:    (id, body)     => api.post(`/pm/bugs/${id}/comments`, { body }),
  getBugActivities: (id)           => api.get(`/pm/bugs/${id}/activities`),
};

// ── QA Management ─────────────────────────────────────────────────────────────
export const qa = {
  // Test Scenarios
  listScenarios:   (projectId)       => api.get(`/qa/projects/${projectId}/test-scenarios`),
  createScenario:  (projectId, data) => api.post(`/qa/projects/${projectId}/test-scenarios`, data),
  updateScenario:  (id, data)        => api.put(`/qa/test-scenarios/${id}`, data),
  deleteScenario:  (id)              => api.delete(`/qa/test-scenarios/${id}`),

  // Test Cases
  getTestCases:   (pid, params) => api.get(`/qa/projects/${pid}/test-cases`, { params }),
  createTestCase: (pid, data)   => api.post(`/qa/projects/${pid}/test-cases`, data),
  getTestCase:    (id)          => api.get(`/qa/test-cases/${id}`),
  updateTestCase: (id, data)    => api.put(`/qa/test-cases/${id}`, data),
  deleteTestCase: (id)          => api.delete(`/qa/test-cases/${id}`),

  // Test Cycles
  getCycles:    (pid)        => api.get(`/qa/projects/${pid}/cycles`),
  createCycle:  (pid, data)  => api.post(`/qa/projects/${pid}/cycles`, data),
  getCycle:     (id)         => api.get(`/qa/cycles/${id}`),
  updateCycle:  (id, data)   => api.put(`/qa/cycles/${id}`, data),
  deleteCycle:  (id)         => api.delete(`/qa/cycles/${id}`),

  // Cycle ↔ Test Case linking
  addCasesToCycle:      (cycleId, data)    => api.post(`/qa/cycles/${cycleId}/cases`, data),
  removeCaseFromCycle:  (cycleId, tcId)    => api.delete(`/qa/cycles/${cycleId}/cases/${tcId}`),

  // Executions
  getExecutions:        (cycleId)        => api.get(`/qa/cycles/${cycleId}/executions`),
  updateExecution:      (execId, data)   => api.put(`/qa/executions/${execId}`, data),
  reportBug:            (execId, data)   => api.post(`/qa/executions/${execId}/report-bug`, data),
  uploadScreenshot:     (execId, file)   => {
    const fd = new FormData(); fd.append('file', file);
    return api.post(`/qa/executions/${execId}/screenshots`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
  },

  // Test management (tester view)
  getMyAssignments: () => api.get('/qa/my-assignments'),

  // Bulk upload
  downloadTemplate:  (pid)        => api.get(`/qa/projects/${pid}/test-cases/template`, { responseType: 'blob' }),
  bulkUploadCases:   (pid, file)  => {
    const fd = new FormData(); fd.append('file', file);
    return api.post(`/qa/projects/${pid}/test-cases/bulk-upload`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
  },

  // Analytics
  getAnalytics: (pid) => api.get(`/qa/projects/${pid}/analytics`),
};

// ── Sales / Bid Management ────────────────────────────────────────────────────
export const salesAPI = {
  getTenders:     (params) => api.get('/sales/tenders', { params }),
  createTender:   (data)   => api.post('/sales/tenders', data),
  getTender:      (id)     => api.get(`/sales/tenders/${id}`),
  updateTender:   (id, d)  => api.put(`/sales/tenders/${id}`, d),
  deleteTender:   (id)     => api.delete(`/sales/tenders/${id}`),

  uploadDocument: (id, data) => api.post(`/sales/tenders/${id}/documents/link`, data),

  getChecklist:    (id)          => api.get(`/sales/tenders/${id}/checklist`),
  addCheckItem:    (id, data)    => api.post(`/sales/tenders/${id}/checklist`, data),
  updateCheckItem: (id, cid, d)  => api.put(`/sales/tenders/${id}/checklist/${cid}`, d),
  deleteCheckItem: (id, cid)     => api.delete(`/sales/tenders/${id}/checklist/${cid}`),

  getVendors:   (params) => api.get('/sales/vendors', { params }),
  createVendor: (data)   => api.post('/sales/vendors', data),
  updateVendor: (id, d)  => api.put(`/sales/vendors/${id}`, d),
  deleteVendor: (id)     => api.delete(`/sales/vendors/${id}`),

  getQuotes:   (id)          => api.get(`/sales/tenders/${id}/quotes`),
  addQuote:    (id, data)    => api.post(`/sales/tenders/${id}/quotes`, data),
  updateQuote: (id, qid, d)  => api.put(`/sales/tenders/${id}/quotes/${qid}`, d),

  getTasks:    (id)          => api.get(`/sales/tenders/${id}/tasks`),
  addTask:     (id, data)    => api.post(`/sales/tenders/${id}/tasks`, data),
  updateTask:  (id, tid, d)  => api.put(`/sales/tenders/${id}/tasks/${tid}`, d),
  deleteTask:  (id, tid)     => api.delete(`/sales/tenders/${id}/tasks/${tid}`),

  getCommunications: (id)       => api.get(`/sales/tenders/${id}/communication`),
  addCommunication:  (id, data) => api.post(`/sales/tenders/${id}/communication`, data),

  getApprovals:   (id)          => api.get(`/sales/tenders/${id}/approvals`),
  addApproval:    (id, data)    => api.post(`/sales/tenders/${id}/approvals`, data),
  updateApproval: (id, aid, d)  => api.put(`/sales/tenders/${id}/approvals/${aid}`, d),

  submitForApproval:   (id)       => api.post(`/sales/tenders/${id}/submit-for-approval`),
  workflowDecision:    (id, data) => api.put(`/sales/tenders/${id}/workflow-decision`, data),
  getPendingApprovals: ()         => api.get('/sales/pending-approvals'),

  getDashboard: () => api.get('/sales/dashboard'),
  getAnalytics: () => api.get('/sales/analytics'),
};

// ── Visitor Management ────────────────────────────────────────────────────────
export const visitorsAPI = {
  getDashboard:     ()       => api.get('/visitors/dashboard/overview'),
  getVisitors:      (params) => api.get('/visitors/', { params }),
  getAllVisitors:    (params) => api.get('/visitors/all', { params }),
  getVisitor:       (id)     => api.get(`/visitors/${id}`),
  updateVisitor:    (id, d)  => api.put(`/visitors/${id}`, d),

  checkIn:          (data)   => api.post('/visitors/checkin', data),
  checkOut:         (id)     => api.post(`/visitors/${id}/checkout`),

  preRegister:      (data)   => api.post('/visitors/pre-register', data),
  getPreRegistered: (params) => api.get('/visitors/pre-registered', { params }),

  blacklist:        (id, data) => api.post(`/visitors/${id}/blacklist`, data),
  unblacklist:      (id)       => api.post(`/visitors/${id}/unblacklist`),
  getBlacklist:     ()         => api.get('/visitors/blacklist/list'),

  getPasses:        (params)       => api.get('/visitors/passes', { params }),
  createPass:       (passNumber)  => api.post('/visitors/passes', { pass_number: passNumber }),
  deletePass:       (id)          => api.delete(`/visitors/passes/${id}`),

  getDailyLog:      (date)   => api.get('/visitors/reports/daily', { params: { report_date: date } }),
  getWeeklySummary: (days)   => api.get('/visitors/reports/summary', { params: { days } }),
};

// ── Biometric Attendance (AMS) ────────────────────────────────────────────────
export const attendanceAPI = {
  getToday:      ()            => api.get('/attendance/today'),
  getRecords:    (params)      => api.get('/attendance/records', { params }),
  sync:          (params)      => api.post('/attendance/sync', null, { params }),
  getDevices:    ()            => api.get('/attendance/devices'),
  getWsStatus:   ()            => api.get('/attendance/ws-status'),
  getDiagnostics: ()           => api.get('/attendance/diagnostics'),
  getEmployee:   (id, params)  => api.get(`/attendance/employee/${id}`, { params }),
  getMy:         (params)      => api.get('/attendance/my', { params }),
  getAmsPersons: ()            => api.get('/attendance/ams-persons'),
  relink:        ()            => api.post('/attendance/relink'),
};

export default api;
